const STICKY_CONTAINER_SELECTOR = '.sticky-history-container';
const MILESTONE_WRAPPER_SELECTOR = '.milestone-wrapper';
const DIALOG_LINK_SELECTOR = '.dialog-link-text';
const DIALOG_SELECTOR = '.milestone-dialog';
const DIALOG_CLOSE_SELECTOR = '.milestone-dialog-close';
const HEADER_HIDDEN_CLASS = 'header--hidden';
const SHOW_DIALOG_CLASS = 'show-dialog';
const ANCHOR_SCROLL_OFFSET_LEFT = 70;

export default class HistoryTimelineScroller {

    constructor() {
        this.stickyContainers = document.querySelectorAll(STICKY_CONTAINER_SELECTOR);
        this.activeHorizontalScrollContainer = null;

        this.handleWindowScroll = this.handleWindowScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleHashChange = this.handleHashChange.bind(this);

        this.handleDialog();
        this.bindEvents();

        window.addEventListener('load', () => {
            this.setupContainers();
            this.handleWindowScroll();
            this.processAnchorScroll();
        });
    }
    /**
     * Sets up the necessary heights for containers to enable
     * vertical scrolling that drives horizontal movement.
     */
    setupContainers() {
        this.stickyContainers.forEach(container => {
            const milestoneWrapper = container.querySelector(MILESTONE_WRAPPER_SELECTOR);
            if (milestoneWrapper) {
                const requiredVerticalScrollHeight = milestoneWrapper.scrollWidth;
                container.style.height = `${requiredVerticalScrollHeight}px`;
            }
        });
    }

    bindEvents() {
        window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
        window.addEventListener('hashchange', this.handleHashChange, { passive: true }); // Hinzugefügt
    }

    /**
     * Handles window resize events to recalculate container heights.
     */
    handleResize() {
        this.setupContainers();
        this.handleWindowScroll();
    }

    /**
     * Handles hash changes in the URL to scroll to the new anchor.
     */
    handleHashChange() {
        this.processAnchorScroll();
    }

    /**
     * Main handler for the window's scroll event.
     * Calculates horizontal scroll based on vertical scroll position.
     */
    handleWindowScroll() {
        const currentWindowScrollY = window.scrollY || document.documentElement.scrollTop;
        const viewportHeight = document.documentElement.clientHeight;
        let overallEffectActive = false;

        this.stickyContainers.forEach(container => {
            const milestoneWrapper = container.querySelector(MILESTONE_WRAPPER_SELECTOR);
            if (!milestoneWrapper) return;

            const parentSection = container.closest('section.stage');

            if (!parentSection) {
                console.warn('History Timeline needs to be in a section nodetype', container);
                return;
            }

            const milestoneWrapperActualHeight = milestoneWrapper.offsetHeight;
            const HORIZONTAL_SCROLL_DELAY_PX = Math.max(0, milestoneWrapperActualHeight - viewportHeight);

            const containerTop = parentSection.offsetTop;
            const containerHeight = container.offsetHeight;
            const effectStartScrollY = containerTop;

            const scrollableDistanceWithinContainer = containerHeight - viewportHeight;

            if (scrollableDistanceWithinContainer <= 0) {
                if (currentWindowScrollY >= effectStartScrollY) {
                    if (milestoneWrapper.scrollLeft !== (milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth)) {
                        milestoneWrapper.scrollLeft = milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth;
                    }
                } else {
                    if (milestoneWrapper.scrollLeft !== 0) {
                        milestoneWrapper.scrollLeft = 0;
                    }
                }
                return;
            }

            if (currentWindowScrollY >= effectStartScrollY && currentWindowScrollY <= (effectStartScrollY + scrollableDistanceWithinContainer)) {
                overallEffectActive = true;
                this.activeHorizontalScrollContainer = container;

                const effectiveHorizontalScrollDelay = Math.min(HORIZONTAL_SCROLL_DELAY_PX, scrollableDistanceWithinContainer);
                const verticalScrollIntoContainer = currentWindowScrollY - effectStartScrollY;

                if (verticalScrollIntoContainer < effectiveHorizontalScrollDelay) {
                    if (milestoneWrapper.scrollLeft !== 0) {
                        milestoneWrapper.scrollLeft = 0;
                    }
                } else {
                    const verticalScrollAfterDelay = verticalScrollIntoContainer - effectiveHorizontalScrollDelay;
                    const effectiveVerticalTrackForHorizontalScroll = scrollableDistanceWithinContainer - effectiveHorizontalScrollDelay;

                    if (effectiveVerticalTrackForHorizontalScroll <= 0) {
                        if (milestoneWrapper.scrollLeft !== (milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth)) {
                            milestoneWrapper.scrollLeft = milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth;
                        }
                    } else {
                        const scrollProgress = verticalScrollAfterDelay / effectiveVerticalTrackForHorizontalScroll;
                        const maxHorizontalScroll = milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth;
                        let newScrollLeft = scrollProgress * maxHorizontalScroll;

                        newScrollLeft = Math.max(0, Math.min(newScrollLeft, maxHorizontalScroll));
                        milestoneWrapper.scrollLeft = newScrollLeft;
                    }
                }

            } else if (currentWindowScrollY < effectStartScrollY && milestoneWrapper.scrollLeft !== 0) {
                milestoneWrapper.scrollLeft = 0;
            } else if (currentWindowScrollY > (effectStartScrollY + scrollableDistanceWithinContainer) && milestoneWrapper.scrollLeft !== (milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth)) {
                milestoneWrapper.scrollLeft = milestoneWrapper.scrollWidth - milestoneWrapper.clientWidth;
            }
        });

        if (!overallEffectActive) {
            this.activeHorizontalScrollContainer = null;
        }

        if (typeof window.updateParallaxEffect === 'function') {
            window.updateParallaxEffect(currentWindowScrollY);
        }
    }

    handleDialog() {
        document.querySelectorAll(DIALOG_LINK_SELECTOR).forEach(wrapper => {
            wrapper.addEventListener('click', () => {
                document.querySelectorAll(DIALOG_SELECTOR).forEach(dialog => {
                    dialog.classList.remove(SHOW_DIALOG_CLASS);
                });
                const targetDialogId = wrapper.getAttribute('data-js');
                if (targetDialogId) {
                    const targetDialog = document.getElementById(targetDialogId);
                    if (targetDialog) {
                        targetDialog.classList.add(SHOW_DIALOG_CLASS);
                    }
                }
                document.body.classList.add(HEADER_HIDDEN_CLASS);
            });
        });

        document.querySelectorAll(DIALOG_CLOSE_SELECTOR).forEach(dialogClose => {
            dialogClose.addEventListener('click', () => {
                document.querySelectorAll(DIALOG_SELECTOR).forEach(dialog => {
                    dialog.classList.remove(SHOW_DIALOG_CLASS);
                });
            });
        });
    }

    /**
     * Processes the current URL hash and scrolls to the target anchor.
     * Can be called on load or on hashchange.
     */
    processAnchorScroll() {
        const hash = window.location.hash;
        if (!hash) return;
        const targetId = hash.substring(1);
        if (!targetId) return;

        requestAnimationFrame(() => {
            let targetMilestoneWrapper = null;
            let targetElementInMilestone = null;
            let parentStickyContainerElement = null;

            for (const container of this.stickyContainers) {
                const wrapper = container.querySelector(MILESTONE_WRAPPER_SELECTOR);
                if (wrapper) {
                    const element = wrapper.querySelector(`#${CSS.escape(targetId)}`);
                    if (element) {
                        targetMilestoneWrapper = wrapper;
                        targetElementInMilestone = element;
                        parentStickyContainerElement = container;
                        break;
                    }
                }
            }

            if (targetMilestoneWrapper && targetElementInMilestone && parentStickyContainerElement) {
                const parentSectionForAnchor = parentStickyContainerElement.closest('section.stage');

                if (!parentSectionForAnchor) {
                    console.warn('History Timeline needs to be in a section nodetype', parentStickyContainerElement);
                    return;
                }

                const viewportHeight = document.documentElement.clientHeight;
                const milestoneWrapperActualHeight_anchor = targetMilestoneWrapper.offsetHeight;
                const scrollableDistanceOverallEffect = parentStickyContainerElement.offsetHeight - viewportHeight;

                if (scrollableDistanceOverallEffect <= 0) {
                    const containerTopSimple = parentSectionForAnchor.offsetTop;
                    window.scrollTo({ top: containerTopSimple, behavior: 'smooth' });
                    setTimeout(() => {
                        const targetElementOffsetLeftSimple = targetElementInMilestone.offsetLeft;
                        const maxHorizontalScrollSimple = targetMilestoneWrapper.scrollWidth - targetMilestoneWrapper.clientWidth;
                        let horizontalScrollTargetSimple = Math.max(0, targetElementOffsetLeftSimple - ANCHOR_SCROLL_OFFSET_LEFT);
                        horizontalScrollTargetSimple = Math.min(horizontalScrollTargetSimple, maxHorizontalScrollSimple);
                        targetMilestoneWrapper.scrollTo({ left: horizontalScrollTargetSimple, behavior: 'smooth' });
                        if (typeof window.updateParallaxEffect === 'function') {
                            window.updateParallaxEffect(window.scrollY || document.documentElement.scrollTop);
                        }
                    }, 150);
                    return;
                }

                const HORIZONTAL_SCROLL_DELAY_PX_anchor = Math.max(0, milestoneWrapperActualHeight_anchor - viewportHeight);
                const effectiveHorizontalScrollDelay_anchor = Math.min(HORIZONTAL_SCROLL_DELAY_PX_anchor, scrollableDistanceOverallEffect);
                const effectiveVerticalTrackForHorizontalScroll_anchor = scrollableDistanceOverallEffect - effectiveHorizontalScrollDelay_anchor;

                const targetElementOffsetLeft = targetElementInMilestone.offsetLeft;
                const maxHorizontalScroll = targetMilestoneWrapper.scrollWidth - targetMilestoneWrapper.clientWidth;

                let horizontalScrollTarget = Math.max(0, targetElementOffsetLeft - ANCHOR_SCROLL_OFFSET_LEFT);
                horizontalScrollTarget = Math.min(horizontalScrollTarget, maxHorizontalScroll);

                const horizontalScrollProgress = maxHorizontalScroll > 0 ? (horizontalScrollTarget / maxHorizontalScroll) : 0;
                const containerTop = parentSectionForAnchor.offsetTop;

                let verticalScrollWithinEffectToAchieveTarget = 0;
                if (effectiveVerticalTrackForHorizontalScroll_anchor > 0) {
                    verticalScrollWithinEffectToAchieveTarget = (horizontalScrollProgress * effectiveVerticalTrackForHorizontalScroll_anchor) + effectiveHorizontalScrollDelay_anchor;
                } else {
                    verticalScrollWithinEffectToAchieveTarget = horizontalScrollProgress > 0 ? scrollableDistanceOverallEffect : effectiveHorizontalScrollDelay_anchor;
                }

                verticalScrollWithinEffectToAchieveTarget = Math.min(verticalScrollWithinEffectToAchieveTarget, scrollableDistanceOverallEffect);
                verticalScrollWithinEffectToAchieveTarget = Math.max(verticalScrollWithinEffectToAchieveTarget, 0);

                let verticalScrollTarget = containerTop + verticalScrollWithinEffectToAchieveTarget;

                verticalScrollTarget = Math.min(verticalScrollTarget, containerTop + scrollableDistanceOverallEffect);
                verticalScrollTarget = Math.max(verticalScrollTarget, containerTop);

                window.scrollTo({
                    top: verticalScrollTarget,
                    behavior: 'smooth'
                });
            }
        });
    }
}
